import { useEffect, useRef } from 'react';
import { extractCSRFToken } from '../utils/csrf';
import { API_ENDPOINTS } from '../config';

/**
 * Hook to fetch and cache CSRF token on component mount
 * Should be used in App.js or a top-level component
 * 
 * Note: In production, CSRF token is obtained from ProfileContext's profile fetch.
 * This hook only runs in development to pre-fetch the token.
 * 
 * Uses a ref to prevent duplicate calls from React StrictMode double-invocation.
 */
export function useCSRF() {
  const hasFetched = useRef(false);

  useEffect(() => {
    // Skip health endpoint in production - ProfileContext will fetch profile and get CSRF token
    const isProduction = process.env.NODE_ENV === 'production' || 
                         (window.location.hostname !== 'localhost' && 
                          window.location.hostname !== '127.0.0.1' && 
                          window.location.hostname !== '0.0.0.0');
    
    if (isProduction) {
      // In production, ProfileContext will handle CSRF token extraction from profile response
      return;
    }

    // Prevent duplicate calls from React StrictMode double-invocation
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    // In development, try to get CSRF token early (optional optimization)
    // ProfileContext will also fetch and get the token, so this is just for early access
    fetch(`${API_ENDPOINTS.API_BASE_URL}/api/health`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(response => {
        extractCSRFToken(response);
      })
      .catch(() => {
        // Silent fail - token will be fetched by ProfileContext or on next API call
      });
  }, []);
}

